import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "@/reducers/types";
import { FreeDrawArrow } from "@/sx-layout/components/plotmap/components/freeDraw/models";
import { Rectangle, UserRole, UserRoles } from "@/sx-layout/components/plotmap/models";
import { useTerm } from "@/sx-layout/hooks";
import { useLoginUser } from "@/sx-layout/hooks/useLoginUser";

export const useSelectedArrow = () => {
  const [freeDrawArrow, setFreeDrawArrow] = useState<FreeDrawArrow>();
  const [rect, setRect] = useState<Rectangle>();

  // 権限制御
  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
  const { isPast } = useTerm(layoutDate);
  const userRole = useSelector<RootState, UserRoles>((state) => state.layoutApp.layoutMasters.user_role);
  const { loginUser } = useLoginUser();
  const isEditable = useMemo(() => {
    const isAdmin = userRole === UserRole.MASTER_ROLE;
    const isUserAndCompany =
      userRole === UserRole.USER_ROLE && freeDrawArrow && freeDrawArrow.company_id === loginUser?.company_id;

    return !isPast && (isAdmin || isUserAndCompany);
  }, [freeDrawArrow, loginUser?.company_id, userRole, isPast]);

  const start = useCallback((arrow: FreeDrawArrow, rect: Rectangle) => {
    setFreeDrawArrow(arrow);
    setRect(rect);
  }, []);

  const end = useCallback(() => {
    setFreeDrawArrow(null);
    setRect(null);
  }, []);

  const changeColorAndThickness = useCallback(
    (arrow: FreeDrawArrow) => {
      setFreeDrawArrow({
        ...freeDrawArrow,
        color: arrow.color,
        thickness: arrow.thickness,
        use_start_hour: arrow.use_start_hour,
        use_end_hour: arrow.use_end_hour,
        timestamp: { ...freeDrawArrow.timestamp, update_date: arrow.timestamp.update_date },
      });
    },
    [freeDrawArrow]
  );

  const changeCoords = useCallback(
    (arrow: FreeDrawArrow) => {
      setFreeDrawArrow({
        ...freeDrawArrow,
        coords: arrow.coords,
        timestamp: { ...freeDrawArrow.timestamp, update_date: arrow.timestamp.update_date },
      });
    },
    [freeDrawArrow]
  );

  const endDrag = (rect: Rectangle) => {
    setRect(rect);
  };

  const centerX = useCallback((): number => {
    if (!rect) return 0;

    return rect.x + rect.width / 2;
  }, [rect]);

  return { freeDrawArrow, isEditable, start, end, changeColorAndThickness, changeCoords, endDrag, centerX };
};
